// src/components/common/Layout.tsx

import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    useTheme,
    useMediaQuery,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    ListItemAvatar,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    CalendarMonth as CalendarIcon,
    People as StudentsIcon,
    Group as GroupsIcon,
    ChevronLeft as ChevronLeftIcon,
    Person as PersonIcon,
    Logout as LogoutIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { isTeacherOrAdmin } from '../../utils/roleUtils';

// Ширина бокового меню
const drawerWidth = 260;

const Layout: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(!isMobile);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    // Меню пользователя
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleProfileClick = () => {
        handleMenuClose();
        navigate('/profile');
    };

    const handleLogout = async () => {
        handleMenuClose();
        await logout();
        navigate('/login', { replace: true });
    };

    // Получить инициалы
    const getInitials = (fullName: string): string => {
        const parts = fullName.trim().split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        if (parts.length === 1) {
            return parts[0][0].toUpperCase();
        }
        return '?';
    };

    // Пункты меню
    const menuItems = [
        { text: 'Дашборд', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'Календарь', icon: <CalendarIcon />, path: '/calendar' },
        { text: 'Ученики', icon: <StudentsIcon />, path: '/students', roles: ['ROLE_TEACHER', 'ROLE_ADMIN'] },
        { text: 'Группы', icon: <GroupsIcon />, path: '/groups', roles: ['ROLE_TEACHER', 'ROLE_ADMIN'] },
        { text: 'О приложении', icon: <InfoIcon />, path: '/about' },   // ← без roles, доступно всем
    ];

    // Содержимое меню
    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Шапка меню с логотипом и кнопкой закрытия */}
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        component="img"
                        src="/favicon.png"
                        alt="Logo"
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            flexShrink: 0,
                        }}
                    />
                    {!isMobile && open && (
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Tutor Platform
                        </Typography>
                    )}
                </Box>
                {!isMobile && (
                    <IconButton onClick={handleDrawerToggle} size="small">
                        <ChevronLeftIcon />
                    </IconButton>
                )}
            </Box>

            {/* Список пунктов меню */}
            <List sx={{ flex: 1, pt: 2 }}>
                {menuItems
                    .filter(item => {
                        // Если у пункта указаны роли — показываем только если у пользователя есть одна из них
                        if ('roles' in item && item.roles) {
                            return user?.roles?.some(r => (item.roles as string[]).includes(r));
                        }
                        return true;
                    })
                    .map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <ListItem key={item.text} disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        navigate(item.path);
                                        if (isMobile) handleDrawerToggle();
                                    }}
                                    sx={{
                                        mx: 1,
                                        borderRadius: 1,
                                        backgroundColor: isActive ? 'primary.light' : 'transparent',
                                        color: isActive ? 'white' : 'text.primary',
                                        '&:hover': {
                                            backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                                        },
                                        justifyContent: !isMobile && !open ? 'center' : 'flex-start',
                                        px: !isMobile && !open ? 1 : 2,
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            color: isActive ? 'white' : 'inherit',
                                            minWidth: !isMobile && !open ? 0 : 40,
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    {(!isMobile && open) && <ListItemText primary={item.text} />}
                                    {isMobile && <ListItemText primary={item.text} />}
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
            </List>

            {/* Футер меню */}
            {(!isMobile && open) && (
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        v1.0.0
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, fontSize: '0.6rem', lineHeight: 1.2 }}
                    >
                        Иконка:{' '}
                        <a
                            href="https://www.flaticon.com/ru/free-icons/"
                            title="обучение иконки"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'underline' }}
                        >
                            juicy_fish
                        </a>
                        {' '}— Flaticon
                    </Typography>
                </Box>
            )}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Верхняя панель (App Bar) */}
            <AppBar
                position="fixed"
                sx={{
                    width: {
                        xs: '100%',
                        sm: open ? `calc(100% - ${drawerWidth}px)` : '100%',
                    },
                    ml: {
                        xs: 0,
                        sm: open ? `${drawerWidth}px` : 0,
                    },
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: 1,
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    transition: theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        {menuItems.find(item => location.pathname === item.path)?.text
                            || (location.pathname === '/profile' ? 'Профиль' : 'Tutor Platform')}
                    </Typography>

                    {/* Аватар с меню пользователя */}
                    {user && (
                        <>
                            <IconButton
                                onClick={handleMenuOpen}
                                size="small"
                                sx={{ ml: 2 }}
                                aria-controls={menuOpen ? 'account-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={menuOpen ? 'true' : undefined}
                            >
                                <Avatar
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: 'primary.main',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                    }}
                                >
                                    {getInitials(user.fullName)}
                                </Avatar>
                            </IconButton>

                            <Menu
                                anchorEl={anchorEl}
                                id="account-menu"
                                open={menuOpen}
                                onClose={handleMenuClose}
                                onClick={handleMenuClose}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                slotProps={{
                                    paper: {
                                        elevation: 3,
                                        sx: {
                                            mt: 1.5,
                                            minWidth: 220,
                                            overflow: 'visible',
                                            '&:before': {
                                                content: '""',
                                                display: 'block',
                                                position: 'absolute',
                                                top: 0,
                                                right: 14,
                                                width: 10,
                                                height: 10,
                                                bgcolor: 'background.paper',
                                                transform: 'translateY(-50%) rotate(45deg)',
                                                zIndex: 0,
                                            },
                                        },
                                    },
                                }}
                            >
                                {/* Информация о пользователе */}
                                <Box sx={{ px: 2, py: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                        <Avatar
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                bgcolor: 'primary.main',
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {getInitials(user.fullName)}
                                        </Avatar>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 600,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {user.fullName}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                @{user.phoneNumber}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Divider />

                                <MenuItem onClick={handleProfileClick} sx={{ py: 1.2 }}>
                                    <ListItemIcon>
                                        <PersonIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Профиль</ListItemText>
                                </MenuItem>

                                <Divider />

                                <MenuItem onClick={handleLogout} sx={{ py: 1.2, color: 'error.main' }}>
                                    <ListItemIcon>
                                        <LogoutIcon fontSize="small" color="error" />
                                    </ListItemIcon>
                                    <ListItemText>Выйти</ListItemText>
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </Toolbar>
            </AppBar>

            {/* Мобильное меню */}
            <Drawer
                variant="temporary"
                open={isMobile && open}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: drawerWidth,
                    },
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Десктопное меню */}
            {!isMobile && (
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            height: '100vh',
                            transform: open ? 'translateX(0)' : `translateX(-${drawerWidth}px)`,
                            transition: theme.transitions.create('transform', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                            visibility: 'visible',
                            zIndex: theme.zIndex.drawer,
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            {/* Основной контент */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    ml: {
                        xs: 0,
                        sm: open ? `${drawerWidth}px` : 0,
                    },
                    backgroundColor: 'background.default',
                    minHeight: '100vh',
                    mt: '64px',
                    display: 'flex',
                    justifyContent: 'center',
                    transition: theme.transitions.create('margin-left', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    width: {
                        xs: '100%',
                        sm: open ? `calc(100% - ${drawerWidth}px)` : '100%',
                    },
                }}
            >
                <Box
                    sx={{
                        py: 3,
                        px: { xs: 2, sm: 3, md: 4 },
                        width: '100%',
                        maxWidth: '1400px',
                    }}
                >
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
};

export default Layout;